import React, { useState } from 'react';
import { apiHelpers } from '../../services/api';
import toast from 'react-hot-toast';

/**
 * AI Mart Assistant
 * 
 * A conversational interface that helps users create data marts
 * by describing what they want in natural language.
 */
const AIMartAssistant = ({ companyId, onMartGenerated, onClose }) => {
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [generatedConfig, setGeneratedConfig] = useState(null);
  const [explanation, setExplanation] = useState('');
  const [error, setError] = useState(null);

  // Example prompts to help users get started
  const examplePrompts = [
    "I want to see monthly sales by product category with total revenue and order count",
    "Create a customer summary showing lifetime value, order count, and average order value",
    "Show me daily appointments by location and doctor, with completion rates",
    "Build a weekly inventory report with stock levels and reorder quantities"
  ];

  const handleGenerate = async () => {
    if (!description.trim()) {
      toast.error('Please describe what data you want to see');
      return;
    }

    setIsLoading(true);
    setError(null);
    setGeneratedConfig(null);

    try {
      const response = await apiHelpers.generateMartWithAI({
        description: description.trim(),
        companyId
      });

      if (response.data.success) {
        setGeneratedConfig(response.data.martConfig);
        setExplanation(response.data.explanation || response.data.martConfig.explanation);
        toast.success('Mart configuration generated!');
      } else {
        setError(response.data.error || 'Failed to generate mart configuration');
      }
    } catch (err) {
      console.error('AI generation error:', err);
      setError(err.response?.data?.error || 'Failed to generate mart configuration');
      toast.error('Failed to generate mart');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseMart = () => {
    if (generatedConfig && onMartGenerated) {
      // Remove the explanation field before passing to the mart form
      const { explanation: _, ...martData } = generatedConfig;
      onMartGenerated(martData);
    }
  };

  const handleReset = () => {
    setDescription('');
    setGeneratedConfig(null);
    setExplanation('');
    setError(null);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-purple-600 to-indigo-600">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">AI Mart Assistant</h2>
              <p className="text-sm text-white/80">Describe what data you want to see</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/20 text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Input Section */}
          {!generatedConfig && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Describe your data mart
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="E.g., I want to see monthly sales by product category with total revenue, order count, and average order value..."
                  rows={4}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-3 dark:bg-gray-700 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  disabled={isLoading}
                />
              </div>

              {/* Example Prompts */}
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Try these examples:</p>
                <div className="flex flex-wrap gap-2">
                  {examplePrompts.map((prompt, index) => (
                    <button
                      key={index}
                      onClick={() => setDescription(prompt)}
                      className="text-xs px-3 py-1.5 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors"
                    >
                      {prompt.length > 50 ? prompt.substring(0, 50) + '...' : prompt}
                    </button>
                  ))}
                </div>
              </div>

              {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
                </div>
              )}
            </>
          )}

          {/* Generated Config Preview */}
          {generatedConfig && (
            <div className="space-y-4">
              {/* Explanation */}
              {explanation && (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-1">AI Explanation</h4>
                  <p className="text-sm text-blue-700 dark:text-blue-400">{explanation}</p>
                </div>
              )}

              {/* Mart Details */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Name</label>
                    <p className="font-mono text-sm text-gray-900 dark:text-white">{generatedConfig.name}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Display Name</label>
                    <p className="text-sm text-gray-900 dark:text-white">{generatedConfig.displayName}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Description</label>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{generatedConfig.description}</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Data Source</label>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {generatedConfig.baseWideView ? (
                      <span className="flex items-center gap-1">
                        <span className="text-purple-600">Wide View:</span> {generatedConfig.baseWideView}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <span className="text-blue-600">Table:</span> {generatedConfig.baseTable}
                      </span>
                    )}
                  </p>
                </div>

                {/* Dimensions */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                    Dimensions ({generatedConfig.dimensions?.length || 0})
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {generatedConfig.dimensions?.map((dim, i) => (
                      <div key={i} className="px-3 py-1.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-lg text-sm">
                        <span className="font-medium">{dim.name}</span>
                        <span className="text-blue-500 dark:text-blue-400 ml-1">({dim.sourceColumn})</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Measures */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                    Measures ({generatedConfig.measures?.length || 0})
                  </label>
                  <div className="space-y-2">
                    {generatedConfig.measures?.map((measure, i) => (
                      <div key={i} className="px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-green-800 dark:text-green-300">{measure.name}</span>
                          <span className="text-xs bg-green-200 dark:bg-green-800 px-2 py-0.5 rounded text-green-700 dark:text-green-300">
                            {measure.format}
                          </span>
                        </div>
                        <code className="text-xs text-green-600 dark:text-green-400 mt-1 block">
                          {measure.expression}
                        </code>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tags */}
                {generatedConfig.tags?.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Tags</label>
                    <div className="flex flex-wrap gap-1">
                      {generatedConfig.tags.map((tag, i) => (
                        <span key={i} className="px-2 py-0.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50">
          {!generatedConfig ? (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={isLoading || !description.trim()}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white rounded-lg flex items-center gap-2 transition-colors"
              >
                {isLoading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Generate Mart
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleReset}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex items-center gap-2 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Try Again
              </button>
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUseMart}
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Use This Mart
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIMartAssistant;

